/**
 * India VIX card — live snapshot + a short daily-history sparkline, shown
 * near the top of the Options Dashboard (it's market-wide, not scoped to
 * NIFTY/BANKNIFTY like the rest of the page's sections). Talks to
 * `/api/v1/india-vix/*` via `indiaVixService`, separate from
 * `optionsDashboardService`.
 *
 * The live "/current" call can genuinely fail (e.g. Breeze session token
 * expired — the backend maps that to a 503) — that failure is shown as a
 * small inline alert scoped to this card, and never blocks the rest of the
 * dashboard from rendering.
 */
import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip as ChartTooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";
import indiaVixService from "@/services/indiaVixService";
import useAuth from "@/hooks/useAuth";
import type { IndiaVixCurrentRead, IndiaVixDailyRead, VixRegime } from "@/types";
import { extractErrorMessage } from "@/utils/errors";
import { trendArrow, trendColor } from "./colors";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ChartTooltip);

// Phase 1 of the approved (2026-08-02) VIX-into-scoring design — this badge
// is purely informational (a percentile-rank label vs. India VIX's own
// trailing history). It never reflects or influences score_v2, confidence_pct,
// or position_size anywhere else in the app.
const REGIME_CHIP_COLOR: Record<VixRegime, "success" | "default" | "warning" | "error"> = {
  LOW: "success",
  NORMAL: "default",
  ELEVATED: "warning",
  EXTREME: "error",
};

const sparklineOptions = {
  responsive: true,
  plugins: { legend: { display: false }, tooltip: { enabled: true } },
  scales: { x: { display: false }, y: { display: false } },
  elements: { point: { radius: 0 } },
};

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

export function VixCard() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole("ADMIN");

  const [current, setCurrent] = useState<IndiaVixCurrentRead | null>(null);
  const [currentError, setCurrentError] = useState<string | null>(null);
  const [currentLoading, setCurrentLoading] = useState(true);

  const [history, setHistory] = useState<IndiaVixDailyRead[]>([]);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const [fromDate, setFromDate] = useState(isoDaysAgo(365));
  const [toDate, setToDate] = useState(isoDaysAgo(0));
  const [syncing, setSyncing] = useState(false);
  const [syncNotice, setSyncNotice] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    try {
      const result = await indiaVixService.getHistory({ from_date: isoDaysAgo(30), to_date: isoDaysAgo(0) });
      setHistory(result.rows);
      setHistoryError(null);
    } catch (err) {
      setHistoryError(extractErrorMessage(err));
    }
  }, []);

  useEffect(() => {
    setCurrentLoading(true);
    indiaVixService
      .getCurrent()
      .then((result) => {
        setCurrent(result);
        setCurrentError(null);
      })
      .catch((err) => setCurrentError(extractErrorMessage(err)))
      .finally(() => setCurrentLoading(false));

    loadHistory();
  }, [loadHistory]);

  const handleSync = async () => {
    setSyncing(true);
    setSyncError(null);
    setSyncNotice(null);
    try {
      const result = await indiaVixService.sync({ from_date: fromDate, to_date: toDate });
      setSyncNotice(`Synced ${result.days_synced} day(s) of India VIX history.`);
      await loadHistory();
    } catch (err) {
      setSyncError(extractErrorMessage(err));
    } finally {
      setSyncing(false);
    }
  };

  const chartData = {
    labels: history.map((row) => row.trade_date),
    datasets: [
      {
        data: history.map((row) => row.close),
        borderColor: "#0F3D63",
        backgroundColor: "transparent",
        borderWidth: 2,
        tension: 0.25,
      },
    ],
  };

  return (
    <Card variant="outlined" sx={{ borderLeft: "6px solid #0F3D63" }}>
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          India VIX
        </Typography>

        {currentError && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            {currentError}
          </Alert>
        )}

        {currentLoading ? (
          <CircularProgress size={24} />
        ) : current ? (
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <Typography variant="h3" fontWeight={800}>
                {current.ltp.toFixed(2)}
              </Typography>
              <Typography variant="body2" sx={{ color: trendColor(current.direction) }}>
                {trendArrow(current.direction)} {current.change >= 0 ? "+" : ""}
                {current.change.toFixed(2)} ({current.change_percent >= 0 ? "+" : ""}
                {current.change_percent.toFixed(2)}%)
              </Typography>
              {current.vix_regime ? (
                <Tooltip title={current.vix_regime_note ?? ""}>
                  <Chip
                    size="small"
                    label={`${current.vix_regime}${current.vix_percentile != null ? ` · ${current.vix_percentile}th pct` : ""}`}
                    color={REGIME_CHIP_COLOR[current.vix_regime]}
                    sx={{ mt: 0.75 }}
                  />
                </Tooltip>
              ) : current.vix_regime_note ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                  {current.vix_regime_note}
                </Typography>
              ) : null}
            </Grid>
            <Grid item xs={12} sm={4}>
              <Stack direction="row" spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Open
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {current.open.toFixed(2)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    High
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {current.high.toFixed(2)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Low
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {current.low.toFixed(2)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Prev Close
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {current.previous_close.toFixed(2)}
                  </Typography>
                </Box>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                As of {new Date(current.captured_at).toLocaleString("en-IN")}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={5}>
              {history.length > 1 ? (
                <Box sx={{ height: 60 }}>
                  <Line data={chartData} options={sparklineOptions} />
                </Box>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {historyError ?? "No history yet — sync below to populate the trend."}
                </Typography>
              )}
            </Grid>
          </Grid>
        ) : (
          <Typography variant="body2" color="text.secondary">
            India VIX is currently unavailable.
          </Typography>
        )}

        {isAdmin && (
          <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              Admin: sync daily history
            </Typography>
            {syncError && (
              <Alert severity="error" sx={{ mb: 1 }} onClose={() => setSyncError(null)}>
                {syncError}
              </Alert>
            )}
            {syncNotice && (
              <Alert severity="success" sx={{ mb: 1 }} onClose={() => setSyncNotice(null)}>
                {syncNotice}
              </Alert>
            )}
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                label="From"
                type="date"
                size="small"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="To"
                type="date"
                size="small"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={handleSync}
                disabled={syncing}
                startIcon={syncing ? <CircularProgress size={14} /> : undefined}
              >
                {syncing ? "Syncing…" : "Sync"}
              </Button>
            </Stack>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default VixCard;
